import * as Sentry from '@sentry/react';
import { noop } from 'lodash-es';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useEventAggregatesMetrics } from 'src/hooks/api/useEventAggregatesMetrics';
import { useEventDiscoveryQuota } from 'src/hooks/api/useEventDiscoveryQuota';
import { useGetCategoryDescription } from 'src/hooks/api/useGetCategoryDescription';
import { useGetCategoryResults } from 'src/hooks/api/useGetCategoryResults';
import { useOnSaleEvents } from 'src/hooks/api/useOnSaleEvents';
import { OnSaleEventWithMetrics } from 'src/navigations/Routes/OnSaleDiscovery/OnSaleDiscoveryView.types';
import { OnSaleEventMetricsName } from 'src/navigations/Routes/OnSaleEvent/OnSaleEvent.types';
import {
  CategoryResult,
  EventAttributes,
  EventDiscoveryQuotaConfig,
  EventMetricsTimestamp,
  EventSectionMetrics,
  OnSaleEventAccess,
  OnSaleEventMetrics,
} from 'src/WebApiController';

export interface IOnSaleEventsContext {
  onSaleEvents: OnSaleEventWithMetrics[];
  event: OnSaleEventWithMetrics | null;
  isLoading: boolean;
  categoryResults: Record<number, CategoryResult>;
  sectionMetrics: EventSectionMetrics[];
  timePeriod: EventMetricsTimestamp;
  metricsName: OnSaleEventMetricsName;
  onSaleEventAccess: OnSaleEventAccess | null;
  isLoadingAccess: boolean;
  onUnlockDemandData: (quotaConfig: EventDiscoveryQuotaConfig) => void;
  onUpdateTimePeriod: (timePeriod: EventMetricsTimestamp) => void;
  setMetricsName: (name: OnSaleEventMetricsName) => void;
}

const emptyContext: IOnSaleEventsContext = {
  onSaleEvents: [],
  event: null,
  isLoading: false,
  categoryResults: {},
  timePeriod: EventMetricsTimestamp.LastWeek,
  sectionMetrics: [],
  metricsName: OnSaleEventMetricsName.Clicks,
  onSaleEventAccess: null,
  isLoadingAccess: false,
  onUnlockDemandData: noop,
  onUpdateTimePeriod: noop,
  setMetricsName: noop,
};

const OnSaleEventsContext = createContext<IOnSaleEventsContext>(emptyContext);

export const useOnSaleEventsContext = () => useContext(OnSaleEventsContext);

export const OnSaleEventsContextProvider = ({
  eventIds,
  activeEventId,
  children,
}: { eventIds: number[]; activeEventId: number } & PropsWithChildren) => {
  const sentrySpan = useRef<Sentry.Span>();
  const [metricsName, setMetricsName] = useState<OnSaleEventMetricsName>(
    OnSaleEventMetricsName.Clicks
  );

  const distinctIds = useMemo(
    () => Array.from(new Set([...eventIds, activeEventId])),
    [activeEventId, eventIds]
  );

  const filterQuery = useMemo(
    () => ({
      eventIds: distinctIds,
      onSaleDates: null,
      timezoneOffsetMins: null,
      viewMode: null,
      searchText: null,
      topLevelCategories: null,
      parentCategories: null,
      performerIds: null,
      venueIds: null,
      countryCodes: null,
      stateProvinceIds: null,
      cityIds: null,
      listingQtyRange: null,
      ticketQtyRange: null,
      priceMinRange: null,
      priceP25Range: null,
      priceP50Range: null,
      visitsL1HRange: null,
      visitsL24HRange: null,
      visitsL3DRange: null,
      visitsL7DRange: null,
      transL1HRange: null,
      transL24HRange: null,
      transL3DRange: null,
      transL7DRange: null,
    }),
    [distinctIds]
  );

  const { onSaleEventMetrics, onSaleEvents, eventAttributes, isLoading } =
    useOnSaleEvents(filterQuery);

  const mergedEvents = useMemo(() => {
    if (!onSaleEvents) {
      return [];
    }
    const { events, performers, venues } = onSaleEvents;

    const eventsWithPerfAndVen: OnSaleEventWithMetrics[] = Object.entries(
      events
    ).map(([_, { event }]) => {
      return {
        event,
        performer: event.perfId ? performers[event.perfId] : undefined,
        venue: event.venId ? venues[event.venId] : undefined,
      };
    });
    if (onSaleEventMetrics) {
      const metricsById = onSaleEventMetrics.reduce(
        (res, metrics) => {
          res[metrics.eId] = metrics;
          return res;
        },
        {} as Record<number, OnSaleEventMetrics>
      );
      for (const onSaleEvent of eventsWithPerfAndVen) {
        if (onSaleEvent.event.viagId) {
          const metrics = metricsById[onSaleEvent.event.viagId];
          if (metrics) {
            Object.assign(onSaleEvent, {
              metrics,
            });
          }
        }
      }
    }

    if (eventAttributes) {
      const eventToAttributes = eventAttributes.reduce(
        (res, attributes) => {
          res[attributes.eventId] = attributes;
          return res;
        },
        {} as Record<number, EventAttributes>
      );
      for (const onSaleEvent of eventsWithPerfAndVen) {
        if (onSaleEvent.event.viagId) {
          const eventAttributes =
            eventToAttributes[onSaleEvent.event.viagId] ?? null;
          Object.assign(onSaleEvent, {
            eventAttributes,
          });
        }
      }
    }

    return eventsWithPerfAndVen;
  }, [eventAttributes, onSaleEventMetrics, onSaleEvents]);

  const categoryIds = useMemo(
    () =>
      Object.keys(onSaleEvents?.performers ?? {})
        .map((strId) => Number(strId))
        .filter((id) => !isNaN(id)),
    [onSaleEvents?.performers]
  );

  const activeEvent = useMemo(
    () =>
      mergedEvents.find(({ event }) => event.viagId === activeEventId) ?? null,
    [activeEventId, mergedEvents]
  );

  const { onSaleEventAccess, unlockDemandData, isLoadingAccess } =
    useEventDiscoveryQuota(activeEvent?.event?.viagId ?? null);

  const { sectionMetrics, timePeriod, onUpdateTimePeriod } =
    useEventAggregatesMetrics(activeEventId, onSaleEventAccess?.recentViewed);

  const { categoryResults } = useGetCategoryResults(categoryIds);
  const { categoryDescription, isLoading: isLoadingDescription } =
    useGetCategoryDescription(
      activeEvent?.event?.genre,
      activeEvent?.performer?.viagId ?? undefined
    );

  useEffect(() => {
    sentrySpan.current = Sentry.startSpan(
      { name: 'Event Discovery', op: 'event_viewed' },
      (span: Sentry.Span) => span
    );
    return () => sentrySpan.current?.end();
  }, []);

  const mergedCategoryResults = useMemo(() => {
    return (categoryResults ?? []).reduce(
      (res, result) => {
        res[result.categoryId] = {
          ...result,
          description:
            activeEvent?.performer?.viagId === result.categoryId
              ? (categoryDescription?.description ?? null)
              : result.description,
        };
        return res;
      },
      {} as Record<number, CategoryResult>
    );
  }, [
    activeEvent?.performer?.viagId,
    categoryDescription?.description,
    categoryResults,
  ]);

  const onUnlockDemandData = useCallback(
    (quotaConfig: EventDiscoveryQuotaConfig) => {
      unlockDemandData(quotaConfig);
      sentrySpan.current?.addEvent(
        'enroll_proceeds_advantage',
        {
          quota: quotaConfig.eventViewQuota,
          markup: quotaConfig.stubHubProceedsAdvantageAmount,
        },
        new Date()
      );
    },
    [unlockDemandData]
  );

  return (
    <OnSaleEventsContext.Provider
      value={{
        isLoading,
        isLoadingAccess,
        onSaleEvents: mergedEvents,
        event: activeEvent,
        metricsName,
        categoryResults: mergedCategoryResults,
        onSaleEventAccess: onSaleEventAccess ?? null,
        setMetricsName,
        onUnlockDemandData,
        sectionMetrics: sectionMetrics ?? [],
        timePeriod,
        onUpdateTimePeriod,
      }}
    >
      {children}
    </OnSaleEventsContext.Provider>
  );
};
